import React, { useState, useEffect } from "react";
import {
  List,
  X,
  WhatsappLogo,
  MapPin,
  EnvelopeSimple,
} from "@phosphor-icons/react";
import { Link, NavLink, useLocation } from "react-router-dom";
import Logo from "./Logo";
import { openWhatsapp, WA_PHONE_DISPLAY } from "../lib/whatsapp";

const NAV_LINKS = [
  { to: "/", label: "Home", end: true },
  { to: "/course", label: "Course" },
  { to: "/batches", label: "Batches" },
  { to: "/packages", label: "Packages" },
  { to: "/mentor", label: "Mentor" },
  { to: "/resources", label: "Resources" },
  { to: "/community", label: "Community" },
  { to: "/decide", label: "Try First" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu on every route change
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#F4F3ED]/85 border-b border-[#D4D0C5]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      {/* Announcement strip — English batch */}
      <Link
        to="/batches"
        data-testid="navbar-announcement"
        className="block w-full bg-[#D9381E] text-white text-center py-1.5 font-mono-acc text-[10px] sm:text-[11px] tracking-[0.25em] uppercase hover:bg-[#0A0A0A] transition-colors"
      >
        <span className="hidden sm:inline">🇬🇧 By popular demand · </span>
        New English batch starts 15 Aug 2026 →
      </Link>
      {/* Top utility strip */}
      <div className="hidden md:flex items-center justify-between gap-8 px-5 lg:px-10 py-2.5 border-b border-[#1A1A1A] bg-[#0A0A0A] text-[#F4F3ED]">
        <div className="flex items-center gap-3 font-mono-acc text-[11px] tracking-[0.25em] uppercase text-[#A1A1AA]">
          <MapPin size={13} weight="duotone" className="text-[#D9381E]" />
          <span data-testid="navbar-locations">Trivandrum · Bengaluru</span>
        </div>
        <div className="flex items-center gap-8">
          <button
            onClick={() => openWhatsapp()}
            data-testid="navbar-whatsapp"
            className="inline-flex items-center gap-2 font-mono-acc text-[11px] tracking-[0.25em] uppercase hover:text-[#25D366] transition-colors cursor-pointer"
          >
            <WhatsappLogo size={13} weight="fill" className="text-[#25D366]" />
            {WA_PHONE_DISPLAY}
          </button>
          <a
            href="mailto:office.greateagle@gmail.com"
            className="hidden lg:inline-flex items-center gap-2 font-mono-acc text-[11px] tracking-[0.25em] uppercase text-[#A1A1AA] hover:text-[#F4F3ED] transition-colors"
          >
            <EnvelopeSimple size={13} weight="duotone" />
            office.greateagle@gmail.com
          </a>
          <a
            href="https://courses.greateagle.co.in/learn"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-student-portal"
            className="hidden md:inline-flex items-center gap-2 font-mono-acc text-[11px] tracking-[0.25em] uppercase text-[#FFB703] hover:text-white transition-colors border border-[#FFB703]/40 px-2.5 py-1"
          >
            Student Portal →
          </a>
          <span className="inline-flex items-center gap-2 font-mono-acc text-[11px] tracking-[0.25em] uppercase text-[#D9381E]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#D9381E] animate-pulse" />
            Cohort 15 · Commences 31 August 2026
          </span>
        </div>
      </div>

      <div className="max-w-[1320px] mx-auto px-5 lg:px-10 h-16 flex items-center justify-between">
        <Link to="/" data-testid="logo-home" className="flex items-center gap-3 group">
          <Logo className="h-11 w-auto" testId="navbar-logo" />
          <div className="hidden sm:flex flex-col items-start leading-tight whitespace-nowrap">
            <span className="font-display font-black text-[16px] tracking-tight">
              GREAT EAGLE TECH
            </span>
            <span className="font-mono-acc text-[10px] tracking-[0.28em] text-[#4A4A4A] uppercase mt-0.5">
              Market Profile · Order Flow
            </span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-4">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `font-body text-[11px] uppercase tracking-[0.15em] transition-colors whitespace-nowrap ${
                  isActive
                    ? "text-[#D9381E]"
                    : "text-[#0A0A0A] hover:text-[#D9381E]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <Link
            to="/community"
            data-testid="navbar-community-cta"
            className="inline-flex items-center gap-1.5 bg-[#D9381E] text-white px-3.5 py-2.5 text-[11px] font-body uppercase tracking-[0.18em] hover:bg-[#0A0A0A] transition-colors whitespace-nowrap"
          >
            <span className="font-mono-acc font-bold">₹999</span>
            Live
          </Link>
          <Link
            to="/packages"
            data-testid="navbar-enroll-btn"
            className="inline-flex items-center gap-2 bg-[#0A0A0A] text-[#F4F3ED] px-4 py-2.5 text-[12px] font-body uppercase tracking-[0.2em] hover:bg-[#D9381E] transition-colors whitespace-nowrap"
          >
            Enroll
            <span className="font-mono-acc">→</span>
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <List size={22} />}
        </button>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-[#F4F3ED] border-t border-[#D4D0C5] px-5 py-4 flex flex-col gap-2"
        >
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-mobile-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-left font-body text-sm uppercase tracking-[0.18em] py-2 ${
                  isActive ? "text-[#D9381E]" : "text-[#0A0A0A]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href="https://courses.greateagle.co.in/learn"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="navbar-student-portal-mobile"
            className="inline-flex items-center gap-2 border border-[#FFB703] text-[#B37A00] px-4 py-3 text-sm font-body uppercase tracking-[0.2em] cursor-pointer text-left mt-2"
          >
            → Student Portal
          </a>
          <button
            onClick={() => { setOpen(false); openWhatsapp(); }}
            data-testid="navbar-whatsapp-mobile"
            className="inline-flex items-center gap-2 border border-[#0A0A0A] px-4 py-3 text-sm font-body uppercase tracking-[0.2em] cursor-pointer text-left mt-2"
          >
            <WhatsappLogo size={16} weight="fill" className="text-[#25D366]" />
            WhatsApp · {WA_PHONE_DISPLAY}
          </button>
        </div>
      )}
    </header>
  );
}
